import { defineStore } from 'pinia'

import { getAvailableIvr, getDetailCampaign, updateCampaign } from '@/api/call-tracking/campaign/campaigns-routes'
import { attachDestinationToMenu } from '@/api/call-tracking/campaign/campaign-actions-routes'

import { getArrayOfUniqueValues, deepCopy } from '@/helpers/app-helper'

import { useDefaultStore } from '@/store/defaultStore'
import { useBreadCrumbsStore } from '@/store/breadCrumbsStore'
import { useDetailCampaignTrackingNumberStore } from '@/store/call-tracking/campaigns/detailCampaignTrackingNumberStore'
import { useDetailCampaignRtbFiltersStore } from '@/store/call-tracking/campaigns/detailCampaignRtbFiltersStore'
import { useDetailCampaignRtbModifiersStore } from '@/store/call-tracking/campaigns/detailCampaignRtbModifiersStore'
import { useDetailCampaignNumberPoolsStore } from '@/store/call-tracking/campaigns/detailCampaignNumberPoolsStore'
import { useDetailNumberPoolTrafficSourcesStore } from '@/store/call-tracking/numberPools/detailNumberPoolTrafficSourcesStore'
import { useDetailCampaignDestinationStore } from '@/store/call-tracking/campaigns/detailCampaignDestinationStore'
import { useDetailCampaignGroupDestinationStore } from '@/store/call-tracking/campaigns/detailCampaignGroupDestinationStore'
import { useDetailCampaignFilterStore } from '@/store/call-tracking/campaigns/detailCampaignFilterStore'
import { useDetailCampaignAccessStore } from '@/store/call-tracking/campaigns/detailCampaignAccessStore'
import { useDetailCampaignTrackingNumberTableStore } from '@/store/call-tracking/campaigns/detailCampaignTrackingNumberTable'
import { useDetailTrafficSourceStore } from '@/store/call-tracking/trafficSources/detailTrafficSourceStore'
import { usePermissionsStore } from '@/store/permissionsStore'
import { useLocalFilterTableStore } from '@/store/localFilterTableStore'
import { useDetailVendorStore } from '@/store/call-tracking/detailVendorStore'

import { VIEWS_OPTIONS } from '@/constants/call-tracking/campaign/campaignSettingsOptions'
import { SETTINGS_HEADER_TABLE } from '@/constants/headersTable/settingsHeaderTable'
import { FORWARDS_CALL_TO_HEADERS_TABLE } from '@/constants/headersTable/forwardsCallToHeadersTable'
import { ROUTING_OPTIONS } from '@/constants/campaignRoutingOptions'
import { ROUTES } from '@/constants/routes'
import {
    FILTER_DESTINATION,
    FILTER_NAME,
    FILTER_STATUS_CUSTOM,
    FILTER_BUYER,
} from '@/constants/localFilters'
import { BC_ACTIONS } from '@/constants/breadCrumbsActions'
import { DUPLICATE_SCOPE_OPTIONS } from '@/constants/sendDuplicateOptions'

export const useDetailCampaignStore = defineStore('detailCampaign', {
    state: () => ({
        slug: null,
        id: null,
        currentView: VIEWS_OPTIONS.setupCampaign,
        loading: true,
        isReloadDetailCampaignBlocked: false,
        updateLoading: false,
        actionLoading: false,
        ivrLoading: false,
        liveLoading: false,
        fullDetailData: null,
        formData: {
            name: null,
            country_id: null,
            status_id: null,

            public: 1,

            ivr_id: null,
            routing_type_id: null,
            dial_attempts: null,
            duplicate_route_id: null,
            duplicates_strict: false,
            duplicate_scope_id: DUPLICATE_SCOPE_OPTIONS.destination.id,
            min_revenue: null,
            max_revenue: null,

            queue_enabled: false,
            queue_audio: null,
            queue_time_limit: null,

            record_calls: null,
            transcription_enabled: null,

            spam_filter: null,
            filter_anonymous: null,
            spam_filter_type: null,
            spam_time: null,
            spam_call_attempts: null,

            filter_enabled: null,

            voip_shield: null,
            voip_black_list_id: null,

            default_vendor_payout_option: null,
            default_vendor_payout_on_option_id: null,
            default_vendor_duplicate_option_id: null,
            default_vendor_length_option: null,
            default_vendor_time_limit_option: null,

            menu_message_type_id: null,
            menu_message_url: null,
            menu_message_text: null,
            menu_message_language: null,
            menu_message_voice: null,

            schedule_enabled: null,
            timezone_id: null,
            schedule: null,
            schedule_voicemail_enabled: null,
            schedule_voicemail_transcription: null,
            schedule_message_type_id: null,
            schedule_message_url: null,
            schedule_message_text: null,
            schedule_message_language: null,
            schedule_message_voice: null,

            greeting_message_enabled: null,
            greeting_message_type_id: null,
            greeting_message_url: null,
            greeting_message_text: null,
            greeting_message_language: null,
            greeting_message_voice: null,

            whisper_message_enabled: null,
            whisper_message_type_id: null,
            whisper_message_url: null,
            whisper_message_text: null,
            whisper_message_language: null,
            whisper_message_voice: null,

            voicemail_message_enabled: null,
            voicemail_voicemail_transcription: null,
            voicemail_message_type_id: null,
            voicemail_message_url: null,
            voicemail_message_text: null,
            voicemail_message_language: null,
            voicemail_message_voice: null,

            sms_reply_enabled: null,
            sms_reply_during_schedule_incoming: null,
            sms_reply_during_schedule_missed_voicemail_enabled: null,
            sms_reply_during_schedule_missed_voicemail_disabled: null,
            sms_reply_after_schedule_incoming: null,
            sms_reply_after_schedule_missed_voicemail_enabled: null,
            sms_reply_after_schedule_missed_voicemail_disabled: null,

            revenue_recovery_enabled: false,
            revenue_recovery_threshold: null,
            revenue_recovery_attempts: null,

            max_concurrency_enabled: false,
            max_concurrency: null,

            cap_enabled: false,
            cap_on_id: null,
            global_cap: null,
            monthly_cap: null,
            daily_cap: null,
            hourly_cap: null,
        },

        availableIvr: [],

        dialogChooseNumber: false,

        currentDigit: null,

        callFilterEditMode: false,
        callFilterUnchanged: true,
        showConfirmDialogBeforeChangeView: false,
        showOverrideConfirmDialogBeforeChangeView: false,

        nextViewSettings: {
            view: null,
            actinName: null,
            BCSettings: null,
            loadFunction: null,
            loadPayload: null,
            preloadFunction: null,
            qnt: 1,
        },
    }),

    getters: {
        destinationsForCounters(state) {
            switch (state.formData.routing_type_id) {
            case ROUTING_OPTIONS.standard.id:
            case ROUTING_OPTIONS.revenue.id:
                return state.fullDetailData ? [
                    ...state.fullDetailData.destinations,
                    ...state.fullDetailData.destination_groups.flatMap((item) => item.destinations),
                ] : []

            case ROUTING_OPTIONS.menu.id:
                return state.fullDetailData ? [
                    ...state.fullDetailData.menu.destinations,
                    ...state.fullDetailData.menu.destination_groups.flatMap((item) => item.destinations),
                ] : []

            default: return []
            }
        },

        destinationsSlugsForCounters() {
            const attachedDestinations = this.destinationsForCounters.map((destination) => destination.slug)

            return getArrayOfUniqueValues(attachedDestinations)
        },

        buyersSlugsForCounters() {
            const attachedBuyers = this.destinationsForCounters.reduce((buyersSlugs, destination) => {
                if (destination.buyer) {
                    buyersSlugs.push(destination.buyer.slug)
                }

                return buyersSlugs
            }, [])

            return getArrayOfUniqueValues(attachedBuyers)
        },

        uniqueBuyersForFilter() {
            const buyersMap = new Map()

            this.destinationsForCounters.forEach((destination) => {
                if (destination.buyer && !buyersMap.has(destination.buyer.id)) {
                    buyersMap.set(destination.buyer.id, {
                        id: destination.buyer.id,
                        name: destination.buyer.name,
                    })
                }
            })

            return Array.from(buyersMap.values())
        },

        callForwardsFilters() {
            const buyerFilter = {
                ...FILTER_BUYER,
                values: this.uniqueBuyersForFilter,
            }

            return [
                FILTER_NAME,
                FILTER_DESTINATION,
                buyerFilter,
                FILTER_STATUS_CUSTOM,
            ]
        },

        callForwardsControlsParams() {
            return {
                filters: this.callForwardsFilters.map((filter) => ({
                    ...filter,
                    rule: null,
                    value: null,
                })),
                sorting: [],
            }
        },
    },

    actions: {
        setCampaignSlug(campaignSlug) {
            this.slug = campaignSlug
        },

        getQueryLocalStorage() {
            const localFilterTableStore = useLocalFilterTableStore()
            localFilterTableStore.initializeTableSettings(SETTINGS_HEADER_TABLE.forwardCallsTo, this.callForwardsFilters)
            localFilterTableStore.setHeaders(SETTINGS_HEADER_TABLE.forwardCallsTo, FORWARDS_CALL_TO_HEADERS_TABLE.map((head, index) => ({
                ...head,
                orderIndex: index,
            })))
            localFilterTableStore.getQueryLocalStorage(SETTINGS_HEADER_TABLE.forwardCallsTo, this.id)
        },

        blockReloadDetailCampaign() {
            this.isReloadDetailCampaignBlocked = true
        },

        setOfferData(offer) {
            if (offer) {
                this.fullDetailData.offer = {
                    applications: this.fullDetailData.offer ? this.fullDetailData.offer.applications : [],
                    ...offer,
                }
            } else {
                this.fullDetailData.offer = null
            }
        },

        async goToLoadDetailCampaign() {
            if (this.isReloadDetailCampaignBlocked) {
                this.isReloadDetailCampaignBlocked = false

                return
            }

            const defaultStore = useDefaultStore()
            const detailCampaignTrackingNumberStore = useDetailCampaignTrackingNumberStore()

            this.loading = true

            const { success, payload, message } = await getDetailCampaign(this.slug)

            if (success) {
                this.parseProps(payload)
                this.setCallFilterGroupsToStore(payload)
                this.updateCallForwardsFilters()

                detailCampaignTrackingNumberStore.setAttachedNumbersAndVendors(payload)
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.loading = false

            return { success, payload }
        },

        setAttachedNumbersAndVendors({ numbers = [], vendors = [], pools = [] }) {
            const detailCampaignTrackingNumberTableStore = useDetailCampaignTrackingNumberTableStore()

            const parseNumbers = detailCampaignTrackingNumberTableStore.parseNumbers({ numbers })
            const parseNumberPools = detailCampaignTrackingNumberTableStore.parseNumberPools({ phone_number_pools: pools })

            detailCampaignTrackingNumberTableStore.setNumbers(parseNumbers)
            detailCampaignTrackingNumberTableStore.setNumberPools(parseNumberPools)
            detailCampaignTrackingNumberTableStore.setVendors(vendors)
        },

        async goToLoadAvailableIVR() {
            const permissionsSore = usePermissionsStore()

            if (!permissionsSore.permissions.callFlows.view) {
                return
            }

            const defaultStore = useDefaultStore()
            this.ivrLoading = true

            const { success, payload, message } = await getAvailableIvr(this.slug)

            if (success) {
                this.availableIvr = payload
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.ivrLoading = false
        },

        parseProps(payload) {
            const detailCampaignTrackingNumberTable = useDetailCampaignTrackingNumberTableStore()
            const detailCampaignDestinationStore = useDetailCampaignDestinationStore()
            const detailCampaignGroupDestinationStore = useDetailCampaignGroupDestinationStore()
            const detailCampaignAccessStore = useDetailCampaignAccessStore()

            this.id = payload.id

            Object.keys(this.formData).forEach((name) => {
                if (!payload.hasOwnProperty(name)) {
                    return
                }
                this.formData[name] = payload[name]
            })

            if (!this.fullDetailData) {
                this.fullDetailData = payload
            } else {
                Object.keys(this.fullDetailData).forEach((name) => {
                    if (!payload.hasOwnProperty(name)) {
                        return
                    }

                    this.fullDetailData[name] = payload[name]
                })
            }

            detailCampaignGroupDestinationStore.setGroupDestination({
                ...payload,
                destination_groups: payload.destination_groups.map((g) => ({ ...g, isGroup: true })),
            })

            detailCampaignDestinationStore.setDestinations(payload)

            const numbers = detailCampaignTrackingNumberTable.parseNumbers(payload)
            const numberPools = detailCampaignTrackingNumberTable.parseNumberPools(payload)

            detailCampaignTrackingNumberTable.setVendors(payload.vendors)
            detailCampaignTrackingNumberTable.setNumbers(numbers)
            detailCampaignTrackingNumberTable.setNumberPools(numberPools)
            detailCampaignAccessStore.setMembers(payload)
        },

        updateRtbMode(type) {
            this.fullDetailData.rtb.type_id = type
        },

        setCallFilterGroupsToStore(payload) {
            const detailCampaignFilterStore = useDetailCampaignFilterStore()

            if (payload.hasOwnProperty('call_filter') && payload.call_filter.hasOwnProperty('groups')) {
                detailCampaignFilterStore.setCallFilterGroups(payload.call_filter.groups)
            } else {
                detailCampaignFilterStore.setCallFilterGroups([])
            }
        },

        async goToUpdateCampaign(formDataForUpdate) {
            const defaultStore = useDefaultStore()
            const detailCampaignTrackingNumberStore = useDetailCampaignTrackingNumberStore()

            this.updateLoading = true

            const formDataForSend = { ...deepCopy(this.formData), ...formDataForUpdate }

            const { success, payload, message } = await updateCampaign({
                formData: formDataForSend,
                slug: this.slug,
            })

            if (success) {
                defaultStore.setSuccessMessage({ message })

                this.parseProps(payload)
                detailCampaignTrackingNumberStore.setAttachedNumbersAndVendors(payload)
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.updateLoading = false

            return { success }
        },

        async goToUpdateLiveTrackingNumber(item, value) {
            const detailCampaignTrackingNumberStore = useDetailCampaignTrackingNumberStore()
            detailCampaignTrackingNumberStore.setTrackingNumber(item)

            this.liveLoading = item.slug

            await detailCampaignTrackingNumberStore.goToUpdateTrackingNumber({
                max_concurrency: value,
            }, this.slug)
        },

        setAttachedDestinations(destinations = []) {
            this.fullDetailData.destinations.unshift(...destinations)
        },

        setAttachedGroups(groups = []) {
            this.fullDetailData.destination_groups.push(...groups)
        },

        setRtbUpdateData(data) {
            this.fullDetailData.rtb = data
        },

        async goToAttachDestinationToMenu(formData) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const { success, payload, message } = await attachDestinationToMenu({ formData, slug: this.slug })

            if (success) {
                defaultStore.setSuccessMessage({ message })

                this._addDestinationToMenu(payload)
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },

        setCurrentView(item) {
            if (item.hasOwnProperty('view')) {
                this.currentView = item.view

                return
            }

            if (item.hasOwnProperty('routeName')) {
                this.router.push({ name: item.routeName })
            }
        },

        setNextViewSettings({
            view, actinName, BCSettings, qnt = 1, loadFunction = null, loadPayload = null, preloadFunction = null,
        }) {
            this.nextViewSettings = {
                view, actinName, BCSettings, qnt, loadFunction, loadPayload, preloadFunction,
            }

            this.applyViewChange()
        },

        applyViewChange() {
            const breadCrumbsStore = useBreadCrumbsStore()
            const detailCampaignRtbFiltersStore = useDetailCampaignRtbFiltersStore()
            const detailCampaignRtbModifiersStore = useDetailCampaignRtbModifiersStore()

            if (!this.callFilterUnchanged) {
                this.setConfirmDialogBeforeChangeView(true)

                return
            }

            if (!detailCampaignRtbFiltersStore.noModify || !detailCampaignRtbModifiersStore.noModify) {
                this.setOverrideConfirmDialogBeforeChangeView(true)

                return
            }

            if (this.nextViewSettings.preloadFunction) {
                this.nextViewSettings.preloadFunction()
            }

            if (this.nextViewSettings.loadFunction) {
                this.nextViewSettings.loadFunction(this.nextViewSettings.loadPayload)
            }

            this.currentView = this.nextViewSettings.view

            breadCrumbsStore[this.nextViewSettings.actinName](this.nextViewSettings.BCSettings, this.nextViewSettings.qnt)
        },

        setConfirmDialogBeforeChangeView(value) {
            this.showConfirmDialogBeforeChangeView = value
        },

        setOverrideConfirmDialogBeforeChangeView(value) {
            this.showOverrideConfirmDialogBeforeChangeView = value
        },

        openSetupTab(view) {
            this.setNextViewSettings({
                view,
                actinName: BC_ACTIONS.setBreadCrumbs,
                BCSettings: [
                    { title: 'Manage Campaigns', routeName: ROUTES.manageCampaigns },
                    { title: 'Setup Campaign', view },
                ],
            })
        },

        openAddUsersToCampaign() {
            this.setNextViewSettings({
                view: VIEWS_OPTIONS.addUsersToCampaign,
                actinName: BC_ACTIONS.setBreadCrumbs,
                BCSettings: [
                    { title: 'Manage Campaigns', routeName: ROUTES.manageCampaigns },
                    { title: 'Setup Campaign', view: VIEWS_OPTIONS.accessCampaign },
                    { title: 'Add Users', view: VIEWS_OPTIONS.addUsersToCampaign },
                ],
            })
        },

        actionDialogChooseNumber(value) {
            this.dialogChooseNumber = value
        },

        openAddTrackingNumbers() {
            const detailCampaignTrackingNumberStore = useDetailCampaignTrackingNumberStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.addTrackingNumbers,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Add Tracking Numbers', view: VIEWS_OPTIONS.addTrackingNumbers },
                loadFunction: detailCampaignTrackingNumberStore.goToLoadAvailableNumbers,
                loadPayload: this.slug,
            })
        },

        openAddNumberPools() {
            const detailCampaignNumberPoolsStore = useDetailCampaignNumberPoolsStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.addNumberPools,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Add Number Pools', view: VIEWS_OPTIONS.addNumberPools },
                loadFunction: detailCampaignNumberPoolsStore.goToLoadAvailableNumberPools,
                loadPayload: this.slug,
            })
        },

        openBuyNumbers() {
            this.setNextViewSettings({
                view: VIEWS_OPTIONS.buyTrackingNumbers,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Buy Tracking Numbers', view: VIEWS_OPTIONS.buyTrackingNumbers },
            })
        },

        openBuyNumbersFromTable() {
            this.setNextViewSettings({
                view: VIEWS_OPTIONS.buyTrackingNumbers,
                actinName: BC_ACTIONS.setBreadCrumbs,
                BCSettings: [
                    { title: 'Manage Campaigns', routeName: ROUTES.manageCampaigns },
                    { title: 'Setup Campaign', view: VIEWS_OPTIONS.setupCampaign },
                    { title: 'Buy Tracking Numbers', view: VIEWS_OPTIONS.buyTrackingNumbers },
                ],
            })
        },

        openDetailNumberPool() {
            this.setNextViewSettings({
                view: VIEWS_OPTIONS.detailNumberPool,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Detail Number Pool', view: VIEWS_OPTIONS.detailNumberPool },
            })
        },

        openAddTrackingNumbersToPool() {
            const detailCampaignTrackingNumberStore = useDetailCampaignTrackingNumberStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.addTrackingNumbersToNumberPool,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Add Numbers to Pool', view: VIEWS_OPTIONS.addTrackingNumbersToNumberPool },
                loadFunction: detailCampaignTrackingNumberStore.goToLoadAvailableNumbers,
                loadPayload: this.slug,
            })
        },

        openAddTrafficSourceToPool() {
            const detailNumberPoolTrafficSourcesStore = useDetailNumberPoolTrafficSourcesStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.addTrafficSourceToPool,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Add Numbers to Pool', view: VIEWS_OPTIONS.addTrackingNumbersToNumberPool },
                loadFunction: detailNumberPoolTrafficSourcesStore.goToLoadAvailableTrafficSources,
            })
        },

        openAttachTrafficSourceToPool() {
            const detailTrafficSourceStore = useDetailTrafficSourceStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.attachTrafficSourceToPool,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Setup Traffic Source', view: VIEWS_OPTIONS.attachTrafficSourceToPool },
                preloadFunction: detailTrafficSourceStore.getQueryLocalStorage,
                loadFunction: detailTrafficSourceStore.goToLoadDetailTrafficSource,
            })
        },

        openAttachTrafficSourceToTrackingNumber() {
            const detailTrafficSourceStore = useDetailTrafficSourceStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.attachTrafficSourceToTrackingNumber,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Setup Traffic Source', view: VIEWS_OPTIONS.attachTrafficSourceToTrackingNumber },
                preloadFunction: detailTrafficSourceStore.getQueryLocalStorage,
                loadFunction: detailTrafficSourceStore.goToLoadDetailTrafficSource,
            })
        },

        openBuyTrackingNumbersToPool() {
            this.setNextViewSettings({
                view: VIEWS_OPTIONS.buyTrackingNumbersToNumberPool,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Add Numbers to Pool', view: VIEWS_OPTIONS.buyTrackingNumbersToNumberPool },
            })
        },

        openDetailTrafficSourceInPool() {
            const detailTrafficSourceStore = useDetailTrafficSourceStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.detailTrafficSourceInPool,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Detail Traffic Source', view: VIEWS_OPTIONS.detailTrafficSourceInPool },
                preloadFunction: detailTrafficSourceStore.getQueryLocalStorage,
                loadFunction: detailTrafficSourceStore.goToLoadDetailTrafficSource,
            })
        },

        openAttachVendorToPool() {
            this.setNextViewSettings({
                view: VIEWS_OPTIONS.attachVendorToPool,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Setup Vendor', view: VIEWS_OPTIONS.attachVendorToPool },
            })
        },

        openDetailTrafficSourceInTrackingNumber() {
            const detailTrafficSourceStore = useDetailTrafficSourceStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.detailTrafficSourceInTrackingNumber,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Detail Traffic Source', view: VIEWS_OPTIONS.detailTrafficSourceInTrackingNumber },
                preloadFunction: detailTrafficSourceStore.getQueryLocalStorage,
                loadFunction: detailTrafficSourceStore.goToLoadDetailTrafficSource,
            })
        },

        openTrackingNumberSettings(number) {
            const detailCampaignTrackingNumberStore = useDetailCampaignTrackingNumberStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.detailTrackingNumber,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Setup Tracking Number', view: VIEWS_OPTIONS.detailTrackingNumber },
                loadFunction: detailCampaignTrackingNumberStore.setTrackingNumber,
                loadPayload: number,
            })
        },

        openTrackingNumberSettingsFromTab(number) {
            const detailCampaignTrackingNumberStore = useDetailCampaignTrackingNumberStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.detailTrackingNumber,
                actinName: BC_ACTIONS.addItemWithBack,
                BCSettings: { title: 'Setup Tracking Number', view: VIEWS_OPTIONS.detailTrackingNumber },
                loadFunction: detailCampaignTrackingNumberStore.setTrackingNumber,
                loadPayload: { ...number, need_verification: true },
            })
        },

        openVerificationTrackingNumber(number) {
            const detailCampaignTrackingNumberStore = useDetailCampaignTrackingNumberStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.verificationTrackingNumber,
                actinName: BC_ACTIONS.addItemWithBack,
                BCSettings: { title: 'Verification', view: VIEWS_OPTIONS.verificationTrackingNumber },
                loadFunction: detailCampaignTrackingNumberStore.setTrackingNumber,
                loadPayload: { ...number, need_verification: true },
            })
        },

        openTrackingNumberOverrideSettings(number) {
            const detailCampaignTrackingNumberStore = useDetailCampaignTrackingNumberStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.detailTrackingNumberOverride,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Setup Tracking Number', view: VIEWS_OPTIONS.detailTrackingNumberOverride },
                loadFunction: detailCampaignTrackingNumberStore.setTrackingNumber,
                loadPayload: number,
            })
        },

        openSetupNewVendorView() {
            this.setNextViewSettings({
                view: VIEWS_OPTIONS.setupNewVendor,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Setup Vendor', view: VIEWS_OPTIONS.setupNewVendor },
            })
        },

        openDetailVendor() {
            const detailVendorStore = useDetailVendorStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.detailVendor,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Detail Vendor', view: VIEWS_OPTIONS.detailVendor },
                qnt: 2,
                loadFunction: detailVendorStore.goToLoadDetailVendor,
            })
        },

        openAddDestinations() {
            const detailCampaignDestinationStore = useDetailCampaignDestinationStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.addDestinations,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Add Destinations', view: VIEWS_OPTIONS.addDestinations },
                loadFunction: detailCampaignDestinationStore.goToLoadAvailableDestinations,
            })
        },

        openAddDestinationToMenu(digit) {
            const detailCampaignDestinationStore = useDetailCampaignDestinationStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.addDestinationToMenu,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Add Destination', view: VIEWS_OPTIONS.addDestinationToMenu },
                preloadFunction: detailCampaignDestinationStore.goToLoadAvailableDestinations,
                loadFunction: this.setCurrentDigit,
                loadPayload: digit,
            })
        },

        openAddGroupToMenu(digit) {
            const detailCampaignGroupDestinationStore = useDetailCampaignGroupDestinationStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.addGroupToMenu,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Add Group', view: VIEWS_OPTIONS.addGroupToMenu },
                preloadFunction: detailCampaignGroupDestinationStore.goToLoadAvailableGroups,
                loadFunction: this.setCurrentDigit,
                loadPayload: digit,
            })
        },

        setCurrentDigit(digit) {
            this.currentDigit = digit
        },

        openAddDestinationGroups() {
            const detailCampaignGroupDestinationStore = useDetailCampaignGroupDestinationStore()

            this.setNextViewSettings({
                view: VIEWS_OPTIONS.addDestinationGroups,
                actinName: BC_ACTIONS.addItem,
                BCSettings: { title: 'Add Destination Groups', view: VIEWS_OPTIONS.addDestinationGroups },
                loadFunction: detailCampaignGroupDestinationStore.goToLoadAvailableGroups,
            })
        },

        changeCallFilterEditMode(value) {
            this.callFilterEditMode = value
        },

        setCallFilterUnchanged(value) {
            this.callFilterUnchanged = value
        },

        updateCallForwardsFilters() {
            const localFilterTableStore = useLocalFilterTableStore()
            localFilterTableStore.initializeTableSettings(SETTINGS_HEADER_TABLE.forwardCallsTo, this.callForwardsFilters)
        },
    },
})
